import { clsx } from "clsx";
import { type FC } from "react";
import {
  ActionButton,
  ActionButtonIntent,
  ActionButtonSize,
  Card,
  CardRowSimple,
  Dialog,
  Icons,
  SymbolIcon,
} from "widgets";

import { type Sample } from "@/graphql.ts";
import { SUBROW_HEIGHT } from "@/settings/settings.js";
import style from "@/ui-modules/core/grid/styles/Cells.module.scss";

import { PopoverController } from "../../overlays/components/OverlayControllers.js";
import { SampleLockedIcon, SampleTile } from "../../tiles/components/SampleTile.js";
import { type IAgGridCellProps } from "../cell-base-interfaces.js";
import { traverseAndCollect } from "../column-props-factory.js";
import ListCellContent, { type ListCellItem } from "./core/ListCellContent.js";
import { TableCell } from "./core/TableCell.js";

const SampleCell: FC<IAgGridCellProps> = (props) => {
  // props.value corresponds to sample.name which is always null in case of hidden samples
  // but we want to handle it differently
  if (props.data == null) {
    return null;
  }

  const { data, colDef, context } = props;
  const value = traverseAndCollect(data, colDef.groupField);

  if (value == null) {
    return null;
  }

  const multiValues: Sample[] = Array.isArray(value) ? value : [value];

  // beyond 2 items we show one single stacked item as a list
  const items: ListCellItem[] =
    multiValues.length > 2
      ? [
          {
            value: <SamplesPopover samples={multiValues} />,
          },
        ]
      : multiValues.map((sample: Sample) => {
          return {
            value: <SamplePopover sample={sample} />,
            tooltip: sample.name,
          };
        });

  return (
    <TableCell>
      <ListCellContent items={items} colDef={colDef} context={context} style={{ height: SUBROW_HEIGHT }} />
    </TableCell>
  );
};

const SamplePopover: FC<{ sample: Sample }> = (props) => {
  return (
    <>
      {props.sample.hidden ? (
        <span style={{ display: "inline-flex", alignItems: "center", columnGap: 3 }}>
          <SymbolIcon icon={"security"} className={clsx(style.cell_file_tag, style.cell_file_tag_error)} />
          [Restricted Sample]
        </span>
      ) : (
        <PopoverController
          overlayOptions={{ placement: "bottom left" }}
          actionWidget={(controllerProps) => (
            <ActionButton
              {...controllerProps}
              icon={<img src={Icons.Action.Sample} width={12} height={12} />}
              size={ActionButtonSize.InTableCell}
              intent={ActionButtonIntent.Outlined}
              aria-label={"Show more details"}
            >
              {props.sample.name}
              {props.sample.locked ? <SampleLockedIcon size={15} /> : null}
            </ActionButton>
          )}
        >
          {(controllerProps) => (
            <Dialog {...controllerProps}>
              <Card scrollable={true}>
                <CardRowSimple>
                  <SampleTile sample={props.sample} />
                </CardRowSimple>
              </Card>
            </Dialog>
          )}
        </PopoverController>
      )}
    </>
  );
};

const SamplesPopover: FC<{ samples: Sample[] }> = (props) => {
  return (
    <PopoverController
      overlayOptions={{ placement: "bottom left" }}
      actionWidget={(controllerProps) => (
        <ActionButton
          {...controllerProps}
          icon={<img src={Icons.Action.Sample} width={12} height={12} />}
          size={ActionButtonSize.InTableCell}
          intent={ActionButtonIntent.Outlined}
          aria-label={"Show more details"}
          label={`${props.samples.length} samples`}
        />
      )}
    >
      {(controllerProps) => (
        <Dialog {...controllerProps}>
          <Card scrollable={true}>
            <CardRowSimple>
              {props.samples.map((sample) => (
                <SampleTile sample={sample} key={sample.id} />
              ))}
            </CardRowSimple>
          </Card>
        </Dialog>
      )}
    </PopoverController>
  );
};

export default SampleCell;
